// Draws the share card in the browser (no server needed). 1080 x 1920, the shape stories and TikTok use.
const W = 1080;
const H = 1920;
const SERIF = "Georgia, 'Times New Roman', serif";
const GOLD = "#D6A848";
const GOLD_DARK = "#96702A";
const CREAM = "#FAF4E8";
const MUTED = "#D9CBD1";

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function sparkle(ctx, x, y, r, fill) {
  const k = r * 0.2;
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + k, y - k);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x + k, y + k);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - k, y + k);
  ctx.lineTo(x - r, y);
  ctx.lineTo(x - k, y - k);
  ctx.closePath();
  ctx.fill();
}

function wrapLines(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth || !cur) cur = test;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

// "Mrs Bird" -> "B", "The Phantom" -> "P", "King George III" -> "G"
export function monogram(role) {
  let s = String(role || "").trim();
  let prev;
  do {
    prev = s;
    s = s.replace(/^(the|mr|mrs|miss|madame|elder|king|dr)\s+/i, "");
  } while (s !== prev);
  return (s[0] || "?").toUpperCase();
}

function background(ctx) {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#4A102C");
  g.addColorStop(1, "#160812");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  for (let x = 0; x < W; x += 6) {
    const a = 0.1 * (0.5 + 0.5 * Math.sin((x / W) * Math.PI * 12 + 0.7));
    ctx.fillStyle = `rgba(0,0,0,${a.toFixed(3)})`;
    ctx.fillRect(x, 0, 6, H);
  }

  const spot = ctx.createRadialGradient(540, 560, 40, 540, 560, 780);
  spot.addColorStop(0, "rgba(255,196,110,0.34)");
  spot.addColorStop(1, "rgba(255,196,110,0)");
  ctx.fillStyle = spot;
  ctx.fillRect(0, 0, W, H);

  const vig = ctx.createRadialGradient(540, 960, 500, 540, 960, 1250);
  vig.addColorStop(0, "rgba(0,0,0,0)");
  vig.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);
}

function bulbs(ctx) {
  const inset = 54;
  const step = 60;
  const pts = [];
  for (let x = inset; x <= W - inset + 1; x += step) pts.push([x, inset], [x, H - inset]);
  for (let y = inset + step; y < H - inset - 30; y += step) pts.push([inset, y], [W - inset, y]);
  ctx.save();
  ctx.shadowColor = "rgba(255,200,110,0.95)";
  ctx.shadowBlur = 20;
  ctx.fillStyle = "#FFE3A0";
  pts.forEach(([x, y]) => {
    ctx.beginPath();
    ctx.arc(x, y, 8, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
  ctx.fillStyle = "#FFFBEA";
  pts.forEach(([x, y]) => {
    ctx.beginPath();
    ctx.arc(x - 2, y - 2, 3.5, 0, Math.PI * 2);
    ctx.fill();
  });
}

function ticket(ctx, text, cx, cy, w, h) {
  const x = cx - w / 2;
  const y = cy - h / 2;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.5)";
  ctx.shadowBlur = 22;
  ctx.shadowOffsetY = 10;
  ctx.beginPath();
  ctx.rect(0, 0, W, H);
  ctx.moveTo(x + 26, cy);
  ctx.arc(x, cy, 26, 0, Math.PI * 2);
  ctx.closePath();
  ctx.moveTo(x + w + 26, cy);
  ctx.arc(x + w, cy, 26, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip("evenodd");
  const g = ctx.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, "#ECC060");
  g.addColorStop(1, "#C49438");
  ctx.fillStyle = g;
  roundedRect(ctx, x, y, w, h, 18);
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = GOLD_DARK;
  ctx.lineWidth = 3;
  ctx.setLineDash([8, 7]);
  [x + 70, x + w - 70].forEach((lx) => {
    ctx.beginPath();
    ctx.moveTo(lx, y + 14);
    ctx.lineTo(lx, y + h - 14);
    ctx.stroke();
  });
  ctx.setLineDash([]);

  ctx.fillStyle = "#10090F";
  ctx.font = `bold 52px ${SERIF}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, cx, cy + 18);
}

function medallion(ctx, cx, cy, r, letter) {
  ctx.save();
  ctx.shadowColor = "rgba(255,190,90,0.75)";
  ctx.shadowBlur = 60;
  ctx.fillStyle = GOLD;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const inner = ctx.createRadialGradient(cx, cy - r * 0.3, 10, cx, cy, r);
  inner.addColorStop(0, "#5A2250");
  inner.addColorStop(1, "#28102C");
  ctx.fillStyle = inner;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 12, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = GOLD_DARK;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 34, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = GOLD;
  ctx.font = `bold 270px ${SERIF}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(letter, cx, cy + 14);
  ctx.textBaseline = "alphabetic";

  sparkle(ctx, cx - r - 50, cy - r + 70, 28, GOLD);
  sparkle(ctx, cx + r + 46, cy - r + 120, 34, CREAM);
  sparkle(ctx, cx + r + 20, cy + r - 40, 24, "#FF758F");
  sparkle(ctx, cx - r - 24, cy + r - 90, 22, CREAM);
}

function fitOneLine(ctx, text, startSize, minSize, maxWidth, style) {
  let size = startSize;
  ctx.font = `${style} ${size}px ${SERIF}`;
  while (ctx.measureText(text).width > maxWidth && size > minSize) {
    size -= 2;
    ctx.font = `${style} ${size}px ${SERIF}`;
  }
  return size;
}

// Works out where everything goes. Level 1 shrinks the medallion to make room for long text.
function layout(ctx, { role, byline, line }, level) {
  const medR = level === 0 ? 215 : 180;
  const medCy = level === 0 ? 560 : 510;
  const iGotY = medCy + medR + 87;

  let roleSize = fitOneLine(ctx, role, 124, 72, 920, "bold");
  let roleLines = [role];
  if (ctx.measureText(role).width > 920) {
    roleSize = 84;
    ctx.font = `bold ${roleSize}px ${SERIF}`;
    roleLines = wrapLines(ctx, role, 900);
  }
  const roleStep = Math.round(roleSize * 1.08);
  const roleY = iGotY + 116;
  const roleEnd = roleY + (roleLines.length - 1) * roleStep;

  const showText = byline;
  let showSize = fitOneLine(ctx, showText, 52, 40, 940, "italic");
  let showLines = [showText];
  if (ctx.measureText(showText).width > 940) {
    showSize = 44;
    ctx.font = `italic ${showSize}px ${SERIF}`;
    showLines = wrapLines(ctx, showText, 900);
  }
  const showY = roleEnd + 80;
  const showEnd = showY + (showLines.length - 1) * Math.round(showSize * 1.2);

  let lineSize = 44;
  ctx.font = `italic ${lineSize}px ${SERIF}`;
  let lineLines = wrapLines(ctx, line, 860);
  if (lineLines.length > 4) {
    lineSize = 40;
    ctx.font = `italic ${lineSize}px ${SERIF}`;
    lineLines = wrapLines(ctx, line, 880);
  }
  lineLines = lineLines.slice(0, 5);
  const lineY = showEnd + 92;
  const lastBaseline = lineY + (lineLines.length - 1) * 58;

  return { medR, medCy, iGotY, roleSize, roleLines, roleStep, roleY, showSize, showLines, showY, lineSize, lineLines, lineY, lastBaseline };
}

// Defaults make the role-quiz card. The show finder passes its own wording.
export async function makeShareCard({
  role, show, line,
  ticketText = "My West End role", lead = "I got", byline = null,
  footerQuestion = "Which West End role are you?", urlText = "mytheatre.uk/quiz",
}) {
  const bylineText = byline ?? `from ${show}`;
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (e) {
      /* the system serif font is used */
    }
  }
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  let L = layout(ctx, { role, byline: bylineText, line }, 0);
  if (L.lastBaseline > 1320) L = layout(ctx, { role, byline: bylineText, line }, 1);

  background(ctx);
  bulbs(ctx);
  ticket(ctx, ticketText, 540, 205, 560, 120);
  medallion(ctx, 540, L.medCy, L.medR, monogram(role));

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = MUTED;
  ctx.font = `italic 46px ${SERIF}`;
  ctx.fillText(lead, 540, L.iGotY);

  ctx.save();
  ctx.fillStyle = CREAM;
  ctx.font = `bold ${L.roleSize}px ${SERIF}`;
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 4;
  L.roleLines.forEach((l, i) => ctx.fillText(l, 540, L.roleY + i * L.roleStep));
  ctx.restore();

  ctx.fillStyle = GOLD;
  ctx.font = `italic ${L.showSize}px ${SERIF}`;
  L.showLines.forEach((l, i) => ctx.fillText(l, 540, L.showY + i * Math.round(L.showSize * 1.2)));

  ctx.fillStyle = CREAM;
  ctx.font = `italic ${L.lineSize}px ${SERIF}`;
  L.lineLines.forEach((l, i) => ctx.fillText(l, 540, L.lineY + i * 58));

  ctx.fillStyle = MUTED;
  ctx.font = `italic 40px ${SERIF}`;
  ctx.fillText(footerQuestion, 540, 1415);

  ctx.save();
  ctx.shadowColor = "rgba(255,190,90,0.8)";
  ctx.shadowBlur = 24;
  ctx.fillStyle = GOLD;
  ctx.font = `bold 72px ${SERIF}`;
  ctx.fillText(urlText, 540, 1488);
  ctx.restore();

  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not make the image"))), "image/png");
  });
}